import React from "react";
import {
  Search,
  User,
  Users,
  Church,
  HeartHandshake,
  HandHeart,
  CalendarDays,
  UserPlus,
  NotebookPen,
  BookOpen,
  LayoutDashboard,
  MapPinned,
  Settings,
  StickyNote,
  Package,
  Droplets,
} from "lucide-react";
import { formatSmartDate } from "@/lib/date";
import Badge from "@/components/Badge";

/**
 * SearchResults — capa de presentación compartida de la búsqueda global.
 *
 * Extraído de GlobalSearchModal.jsx (Fase 1) para que tanto el modal como la
 * futura barra inline del dashboard reusen las mismas piezas SIN duplicar lógica
 * ni estilo. NO contiene estado propio ni toca el motor (useSearchData): es 100%
 * presentacional, recibe la salida del motor por props.
 *
 * Exporta:
 *   - INITIAL_SHOW           constante (ítems por sección antes de "Ver más")
 *   - buildSections          arma las secciones agrupadas por entidad
 *   - HighlightText          resalta el match de la query
 *   - SectionLabel           rótulo de sección + contador
 *   - ResultCard             tarjeta blanca contenedora
 *   - ResultRow              fila icon + título + subtítulo + selección
 *   - CommandIcon            ícono de comandos
 *   - <SearchResults />      (default) región de resultados (query activa)
 */

export const INITIAL_SHOW = 5;

/* ─────────────────────────────────────────
   COMMAND ICON MAP
───────────────────────────────────────── */
const COMMAND_ICONS = {
  "/":            LayoutDashboard,
  "/district":    MapPinned,
  "/settings":    Settings,
  "/agenda":      CalendarDays,
  "/notes":       StickyNote,
};

export function CommandIcon({ route }) {
  const Icon = COMMAND_ICONS[route] ?? UserPlus;
  return <Icon size={16} strokeWidth={2} className="text-blue-600" />;
}

/* ─────────────────────────────────────────
   SECTION CONFIG — add new categories here
───────────────────────────────────────── */
export function buildSections({
  filteredPeople,
  filteredFamilies,
  filteredChurches,
  filteredVisits,
  filteredPrayers,
  filteredEvents,
  filteredNotes = [],
  filteredPreaching = [],
  filteredMaterials = [],
  filteredBaptisms = [],
}) {
  const sections = [];

  if (filteredPeople.length > 0)
    sections.push({
      key: "personas",
      label: "Personas",
      items: filteredPeople.map((p) => ({
        id: p.id,
        title: p.name,
        subtitle: p.phone || "Persona",
        route: `/people/${p.id}`,
        recent: { id: p.id, type: "person", title: p.name, subtitle: p.phone || "Persona", route: `/people/${p.id}` },
        iconBg: "bg-blue-50 dark:bg-blue-900/20", iconColor: "text-blue-600", Icon: User,
      })),
    });

  if (filteredFamilies.length > 0)
    sections.push({
      key: "familias",
      label: "Hogares",
      items: filteredFamilies.map((f) => ({
        id: f.id,
        title: f.name,
        subtitle: f.address || "Hogar",
        route: `/families/${f.id}`,
        recent: { id: f.id, type: "family", title: f.name, subtitle: f.address || "Familia", route: `/families/${f.id}` },
        iconBg: "bg-indigo-50 dark:bg-indigo-900/20", iconColor: "text-indigo-600", Icon: Users,
      })),
    });

  if (filteredChurches.length > 0)
    sections.push({
      key: "iglesias",
      label: "Iglesias",
      items: filteredChurches.map((c) => ({
        id: c.id,
        title: c.name,
        subtitle: c.address || "Iglesia",
        route: `/churches/${c.id}`,
        recent: { id: c.id, type: "church", title: c.name, subtitle: c.address || "Iglesia", route: `/churches/${c.id}` },
        iconBg: "bg-sky-50 dark:bg-sky-900/20", iconColor: "text-sky-600", Icon: Church,
      })),
    });

  if (filteredVisits.length > 0)
    sections.push({
      key: "visitas",
      label: "Visitas",
      items: filteredVisits.map((v) => ({
        id: v.id,
        title: v.contact_name,
        subtitle: formatSmartDate(v.date || v.visit_date),
        route: `/visits/${v.id}`,
        recent: null,
        iconBg: "bg-emerald-50 dark:bg-emerald-900/20", iconColor: "text-emerald-600", Icon: HeartHandshake,
      })),
    });

  if (filteredPrayers.length > 0)
    sections.push({
      key: "oraciones",
      label: "Oraciones",
      items: filteredPrayers.map((p) => ({
        id: p.id,
        title: p.text,
        subtitle: formatSmartDate(p.date || p.created_at),
        route: `/prayers/${p.id}/edit`,
        recent: null,
        iconBg: "bg-rose-50 dark:bg-rose-900/20", iconColor: "text-rose-600", Icon: HandHeart,
      })),
    });

  if (filteredEvents.length > 0)
    sections.push({
      key: "eventos",
      label: "Eventos",
      items: filteredEvents.map((e) => ({
        id: e.id,
        title: e.title,
        subtitle: formatSmartDate(e.date || e.start_date),
        route: `/agenda`,
        recent: null,
        iconBg: "bg-amber-50 dark:bg-amber-900/20", iconColor: "text-amber-600", Icon: CalendarDays,
      })),
    });

  if (filteredNotes.length > 0)
    sections.push({
      key: "notas",
      label: "Notas",
      items: filteredNotes.map((n) => {
        const hasLink = n.linked_type && n.linked_type !== "general" && n.linked_label;
        return {
          id: n.id,
          title: n.title || "Sin título",
          subtitle: hasLink
            ? `${n.linked_label} · ${(n.content || "").slice(0, 40).replace(/\s+/g, " ").trim() || "Nota"}`
            : (n.content || "").slice(0, 60).replace(/\s+/g, " ").trim() || "Nota sin contenido",
          route: `/notes/${n.id}`,
          recent: null,
          iconBg: "bg-indigo-50 dark:bg-indigo-900/20", iconColor: "text-indigo-600", Icon: NotebookPen,
        };
      }),
    });

  if (filteredPreaching.length > 0)
    sections.push({
      key: "predicacion",
      label: "Predicación",
      items: filteredPreaching.map((r) => ({
        id: r.id,
        title: r.title || "Rol de predicación",
        subtitle: r.date_start ? `${r.date_start} – ${r.date_end}` : "",
        route: `/preaching/${r.id}`,
        recent: null,
        iconBg: "bg-emerald-50 dark:bg-emerald-900/20", iconColor: "text-emerald-600", Icon: BookOpen,
      })),
    });

  if (filteredMaterials.length > 0)
    sections.push({
      key: "materiales",
      label: "Materiales",
      items: filteredMaterials.map((m) => ({
        id: m.id,
        title: m.name,
        subtitle: m.category_name || m.material_categories?.name || "Material",
        route: `/materials/${m.id}`,
        recent: null,
        iconBg: "bg-orange-50", iconColor: "text-orange-600", Icon: Package,
      })),
    });

  if (filteredBaptisms.length > 0)
    sections.push({
      key: "bautismos",
      label: "Bautismos",
      items: filteredBaptisms.map((b) => ({
        id: b.id,
        title: b.person_name || b.people?.name || "Persona bautizada",
        subtitle: b.baptism_date ? `Bautizado el ${formatSmartDate(b.baptism_date)}` : "Bautismo",
        route: b.person_id ? `/people/${b.person_id}` : "/baptisms",
        recent: null,
        iconBg: "bg-cyan-50", iconColor: "text-cyan-600", Icon: Droplets,
      })),
    });

  return sections;
}

/* ─────────────────────────────────────────
   HIGHLIGHT
───────────────────────────────────────── */
export function HighlightText({ text, query }) {
  if (!text) return null;
  const q = query?.trim();
  if (!q) return <>{text}</>;
  try {
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(${escaped})`, "i");
    const parts = text.split(re);
    return (
      <>
        {parts.map((part, i) =>
          i % 2 === 1 ? (
            <span key={i} className="bg-blue-100 text-blue-700 rounded-[3px] font-bold">
              {part}
            </span>
          ) : part
        )}
      </>
    );
  } catch {
    return <>{text}</>;
  }
}

/* ─────────────────────────────────────────
   PRESENTATIONAL PRIMITIVES
───────────────────────────────────────── */
export function SectionLabel({ label, count }) {
  return (
    <div className="flex items-center gap-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </p>
      {count != null && count > 0 && (
        <span className="text-[9px] font-bold bg-slate-200/70 text-slate-500 rounded-full px-1.5 py-[2px] leading-none tabular-nums">
          {count}
        </span>
      )}
      <div className="h-px flex-1 bg-slate-200/60" />
    </div>
  );
}

export function ResultCard({ children }) {
  return (
    <div className="bg-white dark:bg-slate-700 rounded-[20px] border border-slate-100 dark:border-slate-700/50 shadow-sm dark:shadow-none overflow-hidden">
      {children}
    </div>
  );
}

export function ResultRow({
  Icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  query,
  isLast,
  isSelected,
  index,
  itemRefs,
  onHover,
  badge,
  onClick,
}) {
  return (
    <button
      ref={(el) => { if (index >= 0) itemRefs.current[index] = el; }}
      onClick={onClick}
      onMouseEnter={() => { if (index >= 0) onHover(index); }}
      className={`
        relative w-full flex items-center gap-3 px-4 py-3
        text-left transition-colors duration-100
        ${!isLast ? "border-b border-slate-100 dark:border-slate-600/50" : ""}
        ${isSelected ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-slate-50/60 dark:hover:bg-slate-600/40 active:bg-slate-50 dark:active:bg-slate-600/40"}
      `}
    >
      {/* SELECTION BAR */}
      {isSelected && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 rounded-r-full bg-blue-400" />
      )}

      {/* ICON */}
      <div className={`h-10 w-10 rounded-[13px] shrink-0 flex items-center justify-center ${iconBg}`}>
        {typeof Icon === "function" ? <Icon /> : <Icon size={16} strokeWidth={2} className={iconColor} />}
      </div>

      {/* TEXT */}
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">
          <HighlightText text={title} query={query} />
        </p>
        {subtitle && (
          <p className="text-[11px] text-slate-400 truncate mt-0.5 leading-none">{subtitle}</p>
        )}
      </div>

      {badge && <div className="shrink-0 ml-1">{badge}</div>}
    </button>
  );
}

/* ─────────────────────────────────────────
   SEARCH RESULTS — región de resultados (query activa)

   Presentacional puro: recibe la salida ya filtrada del motor + handlers.
   Renderiza: comandos → secciones agrupadas → sin-resultados → total.
───────────────────────────────────────── */
export default function SearchResults({
  showCommands,
  matchedCommands,
  visibleSections,
  query,
  selectedIndex,
  expandedSections,
  setExpandedSections,
  itemRefs,
  onHover,
  onNavigate,
  onPickHint,
  hasResults,
  totalResults,
  // Fondo del header sticky de sección — debe coincidir con el del contenedor que
  // monta <SearchResults> para que no haya escalón de color. Default = el del modal/inline.
  stickyBg = "bg-[#f0f5fb] dark:bg-slate-900",
}) {
  return (
    <div className="px-4">

      {/* COMMANDS SECTION */}
      {showCommands && matchedCommands.length > 0 && (() => {
        const cmdOffset = 0;
        return (
          <div>
            <div className={`sticky top-0 z-10 -mx-4 px-4 ${stickyBg} pt-4 pb-1.5`}>
              <SectionLabel label="Comandos" count={matchedCommands.length} />
            </div>
            <ResultCard>
              {matchedCommands.map((cmd, i) => (
                <ResultRow
                  key={cmd.route}
                  Icon={() => <CommandIcon route={cmd.route} />}
                  iconBg="bg-blue-50 dark:bg-blue-900/20"
                  iconColor=""
                  title={cmd.title}
                  subtitle={cmd.subtitle}
                  query={query}
                  isLast={i === matchedCommands.length - 1}
                  isSelected={selectedIndex === cmdOffset + i}
                  index={cmdOffset + i}
                  itemRefs={itemRefs}
                  onHover={onHover}
                  badge={<Badge tone="blue">Comando</Badge>}
                  onClick={() => onNavigate(cmd.route, null)}
                />
              ))}
            </ResultCard>
          </div>
        );
      })()}

      {/* GROUPED SECTIONS */}
      {(() => {
        let offset = showCommands ? matchedCommands.length : 0;
        return visibleSections.map((section) => {
          const sectionStart = offset;
          const isExpanded = expandedSections.has(section.key);
          const visibleItems = isExpanded
            ? section.items
            : section.items.slice(0, INITIAL_SHOW);
          const hiddenCount = section.items.length - visibleItems.length;
          offset += visibleItems.length;

          return (
            <div key={section.key}>
              {/* STICKY SECTION HEADER */}
              <div className={`sticky top-0 z-10 -mx-4 px-4 ${stickyBg} pt-4 pb-1.5`}>
                <SectionLabel label={section.label} count={section.items.length} />
              </div>

              <ResultCard>
                {visibleItems.map((item, i) => {
                  const globalIdx = sectionStart + i;
                  return (
                    <ResultRow
                      key={item.id}
                      Icon={item.Icon}
                      iconBg={item.iconBg}
                      iconColor={item.iconColor}
                      title={item.title}
                      subtitle={item.subtitle}
                      query={query}
                      isLast={i === visibleItems.length - 1 && hiddenCount === 0}
                      isSelected={selectedIndex === globalIdx}
                      index={globalIdx}
                      itemRefs={itemRefs}
                      onHover={onHover}
                      onClick={() => onNavigate(item.route, item.recent)}
                    />
                  );
                })}

                {/* SHOW MORE */}
                {hiddenCount > 0 && (
                  <button
                    onClick={() => {
                      setExpandedSections((prev) => {
                        const next = new Set(prev);
                        next.add(section.key);
                        return next;
                      });
                    }}
                    className="w-full flex items-center justify-center gap-1.5 py-3 text-[12px] font-semibold text-blue-600 hover:bg-blue-50/50 transition-colors"
                  >
                    Ver {hiddenCount} más
                  </button>
                )}
              </ResultCard>
            </div>
          );
        });
      })()}

      {/* NO RESULTS */}
      {!hasResults && (
        <div className="pt-4">
          <div className="bg-white dark:bg-slate-700 rounded-[20px] border border-slate-100 dark:border-slate-700/50 shadow-sm dark:shadow-none p-8 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-slate-50 dark:bg-slate-600 border border-slate-100 dark:border-slate-500 flex items-center justify-center mb-3">
              <Search size={22} className="text-slate-300 dark:text-slate-500" />
            </div>
            <p className="text-[15px] font-semibold text-slate-700 dark:text-slate-200">Sin resultados</p>
            <p className="text-[13px] text-slate-400 mt-1 mb-4">
              No se encontró nada para "{query}".
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {["Iglesias", "Hogares", "Personas"].map((hint) => (
                <button
                  key={hint}
                  onClick={() => onPickHint(hint.toLowerCase())}
                  className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {hint}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TOTAL */}
      {hasResults && totalResults > 0 && (
        <p className="text-center text-[11px] text-slate-400 py-4">
          {totalResults} resultado{totalResults !== 1 ? "s" : ""}
        </p>
      )}

    </div>
  );
}
